import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type NatRule = {
  name?: string;
};

function isValidIp(value: string): boolean {
  const r =
    /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
  return r.test(value);
}

function isValidPort(value: string): boolean {
  if (!/^\d+$/.test(value)) return false;
  const p = Number(value);
  return p >= 1 && p <= 65535;
}

export default function WebUiSettingsNatPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [enabled, setEnabled] = React.useState(false);
  const [rules, setRules] = React.useState<NatRule[]>([]);
  const [nat11, setNat11] = React.useState({
    internalIp: '',
    externalIp: '',
    masquerade: false,
  });
  const [pat, setPat] = React.useState({
    incomingIp: '',
    incomingPort: '',
    proto: 'tcp' as 'tcp' | 'udp',
    destinationIp: '',
    destinationPort: '',
    masquerade: false,
  });
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [state, ruleData] = await Promise.all([
      postWebUiApi<{ enabled?: boolean }>({
        service: 'nat',
        access: 'get',
        command: 'state',
      }),
      postWebUiApi<NatRule[]>({
        service: 'nat',
        access: 'get',
        command: 'rule',
      }),
    ]);
    setEnabled(Boolean(state.enabled));
    setRules(ruleData ?? []);
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const createOneToOneNat = async () => {
    if (!isValidIp(nat11.internalIp) || nat11.internalIp === '0.0.0.0') {
      setError(
        `Device IP Address '${nat11.internalIp}' is not a valid IP Address.`,
      );
      return;
    }
    if (!isValidIp(nat11.externalIp) || nat11.externalIp === '0.0.0.0') {
      setError(
        `External IP Address '${nat11.externalIp}' is not a valid IP Address.`,
      );
      return;
    }
    const name = `1:1 NAT ${nat11.masquerade ? '(Masquerade) ' : ''}${nat11.externalIp} to ${nat11.internalIp}`;
    const params: any = {
      name,
      FORWARD: [
        {
          expr: [
            {
              match: {
                op: '==',
                left: { payload: { protocol: 'ip', field: 'daddr' } },
                right: nat11.internalIp,
              },
            },
            { accept: null },
          ],
        },
        {
          expr: [
            {
              match: {
                op: '==',
                left: { payload: { protocol: 'ip', field: 'saddr' } },
                right: nat11.internalIp,
              },
            },
            { accept: null },
          ],
        },
      ],
      PREROUTING: [
        {
          expr: [
            {
              match: {
                op: '==',
                left: { payload: { protocol: 'ip', field: 'daddr' } },
                right: nat11.externalIp,
              },
            },
            { dnat: { addr: nat11.internalIp } },
          ],
        },
      ],
      POSTROUTING: [
        {
          expr: [
            {
              match: {
                op: '==',
                left: { payload: { protocol: 'ip', field: 'saddr' } },
                right: nat11.internalIp,
              },
            },
            { snat: { addr: nat11.externalIp } },
          ],
        },
      ],
      ips: [nat11.externalIp],
    };
    if (nat11.masquerade) {
      params.POSTROUTING.push({
        expr: [
          {
            match: {
              op: '==',
              left: { payload: { protocol: 'ip', field: 'daddr' } },
              right: nat11.internalIp,
            },
          },
          { masquerade: null },
        ],
      });
    }
    try {
      await postWebUiApi({
        service: 'nat',
        access: 'add',
        command: 'rule',
        parameters: params,
      });
      setMessage('1:1 NAT rule created.');
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const createPat = async () => {
    if (!isValidIp(pat.incomingIp) || pat.incomingIp === '0.0.0.0') {
      setError(
        `Incoming IP Address '${pat.incomingIp}' is not a valid IP Address.`,
      );
      return;
    }
    if (!isValidPort(pat.incomingPort)) {
      setError(`Incoming Port '${pat.incomingPort}' is not valid.`);
      return;
    }
    if (!isValidIp(pat.destinationIp) || pat.destinationIp === '0.0.0.0') {
      setError(
        `Destination IP Address '${pat.destinationIp}' is not a valid IP Address.`,
      );
      return;
    }
    if (!isValidPort(pat.destinationPort)) {
      setError(`Destination Port '${pat.destinationPort}' is not valid.`);
      return;
    }
    const name = `PAT ${pat.masquerade ? '(Masquerade) ' : ''}${pat.proto.toUpperCase()} ${pat.incomingIp}:${pat.incomingPort} to ${pat.destinationIp}:${pat.destinationPort}`;
    const params: any = {
      name,
      FORWARD: [
        {
          expr: [
            {
              match: {
                op: '==',
                left: { payload: { protocol: 'ip', field: 'daddr' } },
                right: pat.destinationIp,
              },
            },
            {
              match: {
                op: '==',
                left: { payload: { protocol: pat.proto, field: 'dport' } },
                right: Number(pat.destinationPort),
              },
            },
            { accept: null },
          ],
        },
        {
          expr: [
            {
              match: {
                op: '==',
                left: { payload: { protocol: 'ip', field: 'saddr' } },
                right: pat.destinationIp,
              },
            },
            {
              match: {
                op: '==',
                left: { payload: { protocol: pat.proto, field: 'sport' } },
                right: Number(pat.destinationPort),
              },
            },
            { accept: null },
          ],
        },
      ],
      PREROUTING: [
        {
          expr: [
            {
              match: {
                op: '==',
                left: { payload: { protocol: 'ip', field: 'daddr' } },
                right: pat.incomingIp,
              },
            },
            {
              match: {
                op: '==',
                left: { payload: { protocol: pat.proto, field: 'dport' } },
                right: Number(pat.incomingPort),
              },
            },
            {
              dnat: {
                addr: pat.destinationIp,
                port: Number(pat.destinationPort),
              },
            },
          ],
        },
      ],
      POSTROUTING: [],
      ips: [],
    };
    if (pat.masquerade) {
      params.POSTROUTING.push({
        expr: [
          {
            match: {
              op: '==',
              left: { payload: { protocol: 'ip', field: 'daddr' } },
              right: pat.destinationIp,
            },
          },
          {
            match: {
              op: '==',
              left: { payload: { protocol: pat.proto, field: 'dport' } },
              right: Number(pat.destinationPort),
            },
          },
          { masquerade: null },
        ],
      });
    }
    try {
      await postWebUiApi({
        service: 'nat',
        access: 'add',
        command: 'rule',
        parameters: params,
      });
      setMessage('Port forwarding rule created.');
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <WebUiPageWrapper titleList={['Settings', 'NAT']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-2">
        <div className="flex w-1/4 flex-col gap-2">
          <h4 className="font-bold">NAT Status</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <td>Setting</td>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>NAT enabled</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    type="checkbox"
                    disabled={readonly}
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton
              disabled={readonly}
              onClick={async () => {
                try {
                  const data = await postWebUiApi<{ enabled?: boolean }>({
                    service: 'nat',
                    access: 'set',
                    command: 'state',
                    parameters: { enabled },
                  });
                  setEnabled(Boolean(data.enabled ?? enabled));
                  setMessage('NAT state updated.');
                  setError(null);
                  if (data.enabled ?? enabled) await reload();
                  else setRules([]);
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              Set NAT Status
            </WebUiButton>
          </div>
          <h4 className="mt-5 font-bold">Create 1:1 NAT</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Parameter</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Device IP Address</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled={readonly || !enabled}
                    value={nat11.internalIp}
                    onChange={(e) =>
                      setNat11((p) => ({ ...p, internalIp: e.target.value }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>External IP Address</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled={readonly || !enabled}
                    value={nat11.externalIp}
                    onChange={(e) =>
                      setNat11((p) => ({ ...p, externalIp: e.target.value }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Masquerade</td>
                <td className="text-center">
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    type="checkbox"
                    disabled={readonly || !enabled}
                    checked={nat11.masquerade}
                    onChange={(e) =>
                      setNat11((p) => ({ ...p, masquerade: e.target.checked }))
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton
              disabled={readonly || !enabled}
              onClick={createOneToOneNat}
            >
              Create 1:1 NAT Rule
            </WebUiButton>
          </div>
        </div>
        <div className="flex w-1/4 flex-col gap-2">
          <h4 className="font-bold">Create Port Forwarding</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Parameter</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Incoming IP Address</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled={readonly || !enabled}
                    value={pat.incomingIp}
                    onChange={(e) =>
                      setPat((p) => ({ ...p, incomingIp: e.target.value }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Incoming Port</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled={readonly || !enabled}
                    value={pat.incomingPort}
                    onChange={(e) =>
                      setPat((p) => ({ ...p, incomingPort: e.target.value }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Protocol</td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    options={[
                      { label: 'TCP', value: 'tcp' },
                      { label: 'UDP', value: 'udp' },
                    ]}
                    disabled={readonly || !enabled}
                    value={pat.proto}
                    onChange={(e) =>
                      setPat((p) => ({
                        ...p,
                        proto: e.target.value as 'tcp' | 'udp',
                      }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Destination IP Address</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    disabled={readonly || !enabled}
                    value={pat.destinationIp}
                    onChange={(e) =>
                      setPat((p) => ({ ...p, destinationIp: e.target.value }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Destination Port</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    className="webui-input w-[90%]"
                    disabled={readonly || !enabled}
                    value={pat.destinationPort}
                    onChange={(e) =>
                      setPat((p) => ({ ...p, destinationPort: e.target.value }))
                    }
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Masquerade</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    width="full"
                    type="checkbox"
                    disabled={readonly || !enabled}
                    checked={pat.masquerade}
                    onChange={(e) =>
                      setPat((p) => ({ ...p, masquerade: e.target.checked }))
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton disabled={readonly || !enabled} onClick={createPat}>
              Create Port Forwarding
            </WebUiButton>
          </div>
        </div>
        <div className="flex w-1/2 flex-col gap-2">
          <h4 className="font-bold">NAT List</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Rule</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, idx) => (
                <tr
                  key={`${rule.name ?? 'rule'}-${idx}`}
                  className="webui-table-row-content"
                >
                  <td>{rule.name ?? `Rule ${idx + 1}`}</td>
                  <td className="text-center">
                    <WebUiButton
                      disabled={readonly || !enabled}
                      onClick={async () => {
                        try {
                          await postWebUiApi({
                            service: 'nat',
                            access: 'remove',
                            command: 'rule',
                            parameters: { index: idx },
                          });
                          setMessage('NAT rule removed.');
                          setError(null);
                          await reload();
                        } catch (err) {
                          setError((err as Error).message);
                        }
                      }}
                    >
                      Remove
                    </WebUiButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div>
            <WebUiButton
              onClick={() =>
                reload().catch((err) => setError((err as Error).message))
              }
            >
              Reload
            </WebUiButton>
          </div>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
